import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  ContractorBumperUsersDto,
  ContractorDto,
  ContractorFullCreateDto,
  ContractorFullDto,
  ContractorFullUpdateDto,
  ContractorPaymentTypeEnum,
  ContractorRegionDto,
  ContractorUserDto,
  RegionDto,
  UserDto,
} from '@vatos-pas/common';
import { AppRole } from 'providers/roles';
import { axiosInstance } from 'services/http';
import { extractErrorMessage } from 'utils';

export interface RegionOption {
  id: string;
  name: string;
}

export interface BumperOption {
  id: string;
  name: string;
  active?: boolean;
}

export interface ContractorFormValues {
  name: string;
  corporation: string;
  paymentType: ContractorPaymentTypeEnum | '';
  paymentRate: number | null;
  bumperPaymentType: ContractorPaymentTypeEnum | '';
  bumperPaymentRate: number | null;
  hangerAvailable: boolean;
  finisherAvailable: boolean;
  sprayerAvailable: boolean;
  trimAvailable: boolean;
  paintAvailable: boolean;
  bumperAvailable: boolean;
  specialistAvailable: boolean;
  hangerCapacity: number | null;
  finisherCapacity: number | null;
  sprayerCapacity: number | null;
  trimCapacity: number | null;
  paintCapacity: number | null;
  bumperCapacity: number | null;
  active: boolean;
  suspended: boolean;
  canPerformHighCeilingTasks: boolean;
  notes: string;
  selectedRegions: string[];
  selectedBumpers: string[];
}

export type ContractorMutationPayload = Partial<
  Omit<ContractorDto, 'id' | 'createdAt' | 'updatedAt'>
>;

function useContractor() {
  const dataProvider = useDataProvider();

  const [contractorId, setContractorId] = useState<string>('');
  const {
    data: selectedContractor,
    loading: contractorLoading,
    loaded: contractorLoaded,
    error: contractorError,
  } = useGetOne<ContractorDto>('contractor', contractorId, {
    enabled: !!contractorId,
  });

  const isLoading = !!contractorId && !selectedContractor && contractorLoading;

  const [formLoading, setFormLoading] = useState(false);
  const [contractorRegions, setContractorRegions] = useState<
    ContractorRegionDto[]
  >([]);
  const [contractorUsers, setContractorUsers] = useState<ContractorUserDto[]>(
    [],
  );
  const [contractorRegionsLoaded, setContractorRegionsLoaded] = useState(false);
  const [contractorUsersLoaded, setContractorUsersLoaded] = useState(false);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [bumpersError, setBumpersError] = useState<string | null>(null);

  const loadContractorById = useCallback((id: string): void => {
    setContractorId(id);
  }, []);

  /**
   * One request: POST /contractor/full persists the contractor, its regions
   * and its bumper links in a single API transaction (the old POST contractor
   * → N POST contractor-region chain left partially linked contractors behind
   * when a later call failed, and a retry created a duplicate).
   */
  const createContractor = useCallback(
    async (
      data: ContractorMutationPayload,
      regionIds: string[],
      bumperUserIds?: string[],
    ): Promise<ContractorFullDto> => {
      const payload: ContractorFullCreateDto = {
        contractor: data as ContractorFullCreateDto['contractor'],
        regionIds,
        ...(bumperUserIds !== undefined && { bumperUserIds }),
      };
      const result = await axiosInstance.post<ContractorFullDto>(
        'contractor/full',
        payload,
      );
      setContractorId(result.data.id);
      return result.data;
    },
    [],
  );

  /**
   * One request: PATCH /contractor/:id/full. Omitted keys are left untouched
   * server-side, so a capacity-only caller (Regional Manager) sends just the
   * contractor fields and never the relation sets. Bumper links travel as
   * explicit add/remove deltas: the API never unlinks a bumper this form did
   * not list in `remove`.
   */
  const updateContractor = useCallback(
    async (
      id: string,
      data: ContractorMutationPayload,
      relations?: {
        regionIds: string[];
        bumperUsers?: ContractorBumperUsersDto;
      },
    ): Promise<ContractorFullDto> => {
      if (!selectedContractor) {
        throw new Error('Cannot update: contractor data not loaded');
      }
      const payload: ContractorFullUpdateDto = {
        contractor: data as ContractorFullUpdateDto['contractor'],
        ...(relations && {
          regionIds: relations.regionIds,
          ...(relations.bumperUsers !== undefined && {
            bumperUsers: relations.bumperUsers,
          }),
        }),
      };
      const result = await axiosInstance.patch<ContractorFullDto>(
        `contractor/${id}/full`,
        payload,
      );
      return result.data;
    },
    [selectedContractor],
  );

  const loadRegionOptions = useCallback(
    async (_searchTerm: string): Promise<RegionOption[]> => {
      try {
        const result = await dataProvider.getList<RegionDto>('region', {
          filter: {},
          pagination: { page: 1, perPage: 999 },
          sort: { field: 'name', order: 'ASC' },
        });
        setRegionsError(null);
        return result.data.map(region => ({
          id: region.id,
          name: region.name,
        }));
      } catch (error) {
        console.error('Failed to load regions:', error);
        setRegionsError(extractErrorMessage(error, 'Failed to load regions'));
        return [];
      }
    },
    [dataProvider],
  );

  /**
   * Candidates: active unlinked bumpers, plus every bumper already linked to
   * this contractor whatever its state, so an inactive linked bumper still
   * shows as a chip and can only leave through an explicit removal.
   */
  const loadBumperOptions = useCallback(
    async (contractorId: string): Promise<BumperOption[]> => {
      try {
        const result = await dataProvider.getList<UserDto>('user', {
          filter: {
            s: {
              $and: [
                { 'userRoles.role.name': { $eq: AppRole.Bumper } },
                {
                  $or: [
                    {
                      $and: [
                        { active: true },
                        { 'contractorUser.contractorId': { $isnull: true } },
                      ],
                    },
                    { 'contractorUser.contractorId': { $eq: contractorId } },
                  ],
                },
              ],
            },
          },
          pagination: { page: 1, perPage: 200 },
          sort: { field: 'firstName', order: 'ASC' },
        });
        setBumpersError(null);
        return result.data.map(user => ({
          id: user.id,
          name: user.active
            ? `${user.firstName} ${user.lastName}`
            : `${user.firstName} ${user.lastName} (inactive)`,
          active: user.active,
        }));
      } catch (error) {
        console.error('Failed to load bumpers:', error);
        setBumpersError(extractErrorMessage(error, 'Failed to load bumpers'));
        return [];
      }
    },
    [dataProvider],
  );

  const loadContractorRegionsForContractor = useCallback(
    async (contractorId: string): Promise<ContractorRegionDto[]> => {
      try {
        const result = await dataProvider.getList<ContractorRegionDto>(
          'contractor-region',
          {
            filter: { contractorId },
            pagination: { page: 1, perPage: 999 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setContractorRegions(result.data);
        setContractorRegionsLoaded(true);
        setRegionsError(null);
        return result.data;
      } catch (error) {
        console.error('Failed to load contractor regions:', error);
        setContractorRegions([]);
        setRegionsError(
          extractErrorMessage(error, 'Failed to load contractor regions'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const loadContractorUsersForContractor = useCallback(
    async (contractorId: string): Promise<ContractorUserDto[]> => {
      try {
        const result = await dataProvider.getList<ContractorUserDto>(
          'contractor-user',
          {
            filter: { contractorId },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'ASC' },
          },
        );
        setContractorUsers(result.data);
        setContractorUsersLoaded(true);
        setBumpersError(null);
        return result.data;
      } catch (error) {
        console.error('Failed to load contractor users:', error);
        setContractorUsers([]);
        setBumpersError(
          extractErrorMessage(error, 'Failed to load contractor bumpers'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const resetLoadedFlags = useCallback(() => {
    setContractorRegionsLoaded(false);
    setContractorUsersLoaded(false);
  }, []);

  return {
    selectedContractor,
    isLoading,
    contractorLoaded,
    contractorError,
    formLoading,
    contractorRegions,
    contractorUsers,
    contractorRegionsLoaded,
    contractorUsersLoaded,
    regionsError,
    bumpersError,
    loadContractorById,
    createContractor,
    updateContractor,
    loadRegionOptions,
    loadBumperOptions,
    loadContractorRegionsForContractor,
    loadContractorUsersForContractor,
    setFormLoading,
    resetLoadedFlags,
  };
}

export const ContractorContainer = createContainer(useContractor);
